import { lazy, Suspense, useEffect, useState } from 'react';
import { Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { isEmpty, isNil } from 'lodash';

import { getToken, signOut } from '~/modules/token';

import { Login } from '~/components/pages/LoginPage';
import { Index } from '~/components/pages/DashboardPage';
import { Services } from '~/components/pages/ServicesPage';
import { ServiceMovePage, ServiceUpdatePage } from '~/components/pages/ServiceActionPage';
import { MLS } from '~/components/pages/FeedsPage';
import FeedDetailPage from '~/components/pages/FeedDetailPage';

import globalState, { useAppDispatch } from '~/state/global';

import { decodeToken } from '~/modules/access';
import { isSessionExpired, isSessionExpiring } from '~/lib/session';
import { useRefreshSessionMutation } from '~/modules/query/auth';
import { VideoDashboard } from '~/components/pages/ListingVideosPage';
import { Customers } from '~/components/pages/CustomersPage';
import { OnboardPage } from '~/components/pages/OnboardPage';
import { EnableSocialPage } from '~/components/pages/enable-social/EnableSocialPage';
import { DisconnectSocialPage } from '~/components/pages/disconnect-social/DisconnectSocialPage';
import { Utilities } from '~/components/pages/UtilitiesPage';
import { MlsAgreements } from '~/components/pages/MlsAgreementsPage';
import CustomerPage from '~/components/pages/mls-agreements/CustomerPage';
import MembershipPage from '~/components/pages/mls-agreements/MembershipPage';
import MlsPage from '~/components/pages/mls-agreements/MlsPage';
import { FeatureFlags } from '~/components/pages/FeatureFlagsPage';
import AttributePage from '~/components/pages/feature-flags/AttributePage';
import EnvironmentPage from '~/components/pages/feature-flags/EnvironmentPage';
import FlagPage from '~/components/pages/feature-flags/FlagPage';
import KnownUserPage from '~/components/pages/feature-flags/KnownUserPage';
import ProductPage from '~/components/pages/feature-flags/ProductPage';
import RequestPage from '~/components/pages/feature-flags/RequestPage';
import UpdatePage from '~/components/pages/feature-flags/UpdatePage';
import { NotFoundPage } from '~/components/pages/NotFoundPage';
import Overview from '~/components/pages/ai/AiOverviewPage';
import AnalyticsPage from '~/components/pages/ai/analytics/AnalyticsPage';
import PlacementCreatePage from '~/components/pages/ai/placements/PlacementCreatePage';
import PlacementEditorPage from '~/components/pages/ai/placements/PlacementEditorPage';
import PlacementsPage from '~/components/pages/ai/placements/PlacementsPage';
import PersonaCreatePage from '~/components/pages/ai/personas/PersonaCreatePage';
import PersonaDetailPage from '~/components/pages/ai/personas/PersonaDetailPage';
import PersonasPage from '~/components/pages/ai/personas/PersonasPage';
import DigitalAssistantCreatePage from '~/components/pages/ai/digital-assistants/DigitalAssistantCreatePage';
import DigitalAssistantDetailPage from '~/components/pages/ai/digital-assistants/DigitalAssistantDetailPage';
import DigitalAssistantsPage from '~/components/pages/ai/digital-assistants/DigitalAssistantsPage';
import SettingsPage from '~/components/pages/ai/settings/SettingsPage';
import CaptionTestPage from '~/components/pages/ai/tools/CaptionTestPage';
import { SendTestPage } from '~/components/pages/notifications/SendTestPage';
import ProviderKeyCreatePage from '~/components/pages/notifications/provider-keys/ProviderKeyCreatePage';
import ProviderKeyDetailPage from '~/components/pages/notifications/provider-keys/ProviderKeyDetailPage';
import ProviderKeysPage from '~/components/pages/notifications/provider-keys/ProviderKeysPage';
import TemplateCreatePage from '~/components/pages/notifications/templates/TemplateCreatePage';
import TemplateDetailPage from '~/components/pages/notifications/templates/TemplateDetailPage';
import TemplatesPage from '~/components/pages/notifications/templates/TemplatesPage';
import { AppLayout } from '~/app/shell/AppLayout';
import { GUARDS } from '~/app/access';
import { RequireAccess } from '~/app/guards';
import { legacyDetailTarget, REDIRECTS } from '~/app/redirects';
import { routes } from '~/app/routes';
import { Modal, ModalActions, Button } from '~/components/ui';

// Dev only: Vite drops the whole branch (and the lazy chunk) from production builds.
const KitPage = import.meta.env.DEV
  ? lazy(() => import('~/ui-kit/KitPage').then((m) => ({ default: m.KitPage })))
  : null;

/** A moved link: same query string, new path. */
const Redirect = ({ to }: { to: string }) => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
};

type SessionPhase = 'active' | 'expiring' | 'expired';

export const App = () => {
  const hasToken = !isNil(getToken());
  const { pathname, search } = useLocation();
  // One expiry state; each modal below is open for exactly one phase.
  const [session, setSession] = useState<SessionPhase>('active');

  const userState = globalState.useUserState();
  const notificationState = globalState.useNotificationState();

  const globalDispatch = useAppDispatch();

  const [refreshSession] = useRefreshSessionMutation();

  useEffect(() => {
    if (!hasToken) return;

    const timer = setInterval(() => {
      // Read per tick: every response can rotate the token, and with it the refresh expiry.
      const jwt = decodeToken(getToken());

      // An undecodable token is treated the same as no token: straight to the login page.
      if (isNil(jwt)) {
        clearInterval(timer);
        signOut();
        return;
      }

      if (isSessionExpired(jwt.expRefresh)) {
        clearInterval(timer);
        setSession('expired');
      } else if (isSessionExpiring(jwt.expRefresh)) {
        setSession('expiring');
      }
    }, 5000);

    return () => clearInterval(timer);
  }, [hasToken]);

  const handleSessionExpiringModalClose = async () => {
    try {
      const resp = await refreshSession().unwrap();

      if (resp.status === 0) {
        setSession('active');
      } else {
        signOut();
      }
    } catch {
      signOut();
    }
  };

  // The kit shows static primitives only, so it renders without signing in.
  if (KitPage && pathname === routes.uiKit) {
    return (
      <Suspense fallback={null}>
        <KitPage />
      </Suspense>
    );
  }

  if (isNil(userState) || isEmpty(userState) || isNil(getToken())) {
    return <Login />;
  } else {
    // Old `?detail=` links go to their record route once that route exists (app/redirects.ts).
    const legacy = legacyDetailTarget(search);
    if (legacy) {
      return <Navigate to={legacy} replace />;
    }

    return (
      <>
        <Routes>
          <Route element={<AppLayout />}>
            <Route path="/" element={<Index />} />
            <Route path="/feeds" element={<MLS />} />
            <Route path="/feeds/:id" element={<FeedDetailPage />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:id/update" element={<ServiceUpdatePage />} />
            <Route path="/services/:id/move" element={<ServiceMovePage />} />
            <Route path="/listing-videos" element={<VideoDashboard />} />
            <Route path="/customers" element={<Customers />} />
            <Route element={<RequireAccess {...GUARDS.customerOnboard} />}>
              <Route path="/customers/new" element={<OnboardPage />} />
            </Route>
            <Route path="/customers/:id/enable-social" element={<EnableSocialPage />} />
            <Route path="/customers/:id/disconnect-social" element={<DisconnectSocialPage />} />
            <Route path="/utilities" element={<Utilities />} />
            <Route element={<RequireAccess {...GUARDS.mlsAgreements} />}>
              <Route path="/mls-agreements" element={<MlsAgreements />} />
              <Route path="/mls-agreements/mls/new" element={<MlsPage />} />
              <Route path="/mls-agreements/customers/new" element={<CustomerPage />} />
              <Route path="/mls-agreements/memberships/new" element={<MembershipPage />} />
              <Route path="/mls-agreements/mls/:id" element={<MlsPage />} />
              <Route path="/mls-agreements/customers/:id" element={<CustomerPage />} />
              <Route path="/mls-agreements/memberships/:id" element={<MembershipPage />} />
            </Route>
            <Route element={<RequireAccess {...GUARDS.featureFlags} />}>
              <Route path="/feature-flags" element={<FeatureFlags />} />
              <Route path="/feature-flags/admin/products/new" element={<ProductPage />} />
              <Route path="/feature-flags/admin/products/:id" element={<ProductPage />} />
              <Route path="/feature-flags/admin/environments/new" element={<EnvironmentPage />} />
              <Route path="/feature-flags/admin/environments/:id" element={<EnvironmentPage />} />
              <Route path="/feature-flags/admin/attributes/new" element={<AttributePage />} />
              <Route path="/feature-flags/admin/attributes/:id" element={<AttributePage />} />
              <Route path="/feature-flags/admin/:area" element={<FeatureFlags />} />
              <Route path="/feature-flags/flags/new" element={<FlagPage />} />
              <Route path="/feature-flags/updates/new" element={<UpdatePage />} />
              <Route path="/feature-flags/users/new" element={<KnownUserPage />} />
              <Route path="/feature-flags/flags/:id" element={<FlagPage />} />
              <Route path="/feature-flags/updates/:id" element={<UpdatePage />} />
              <Route path="/feature-flags/requests/:id" element={<RequestPage />} />
              <Route path="/feature-flags/users/:id" element={<KnownUserPage />} />
              <Route path="/feature-flags/:section" element={<FeatureFlags />} />
            </Route>

            <Route path="/ai" element={<RequireAccess {...GUARDS.ai} />}>
              <Route index element={<Overview />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="placements" element={<PlacementsPage />} />
              <Route path="placements/new" element={<PlacementCreatePage />} />
              <Route path="placements/:placementKey" element={<PlacementEditorPage />} />
              <Route path="personas" element={<PersonasPage />} />
              <Route path="personas/new" element={<PersonaCreatePage />} />
              <Route path="personas/:id" element={<PersonaDetailPage />} />
              <Route path="digital-assistants" element={<DigitalAssistantsPage />} />
              <Route path="digital-assistants/new" element={<DigitalAssistantCreatePage />} />
              <Route path="digital-assistants/:id" element={<DigitalAssistantDetailPage />} />
              <Route path="settings/:section" element={<SettingsPage />} />
              {/* Reachable by URL only; developer tool, deliberately absent from the sidebar. */}
              <Route path="caption-test" element={<CaptionTestPage />} />
            </Route>

            <Route path="/notifications" element={<RequireAccess {...GUARDS.notifications} />}>
              <Route index element={<Navigate to={routes.notificationTemplates} replace />} />
              <Route path="templates" element={<TemplatesPage />} />
              <Route path="templates/new" element={<TemplateCreatePage />} />
              <Route path="templates/:id" element={<TemplateDetailPage />} />
              <Route path="provider-keys" element={<ProviderKeysPage />} />
              <Route path="provider-keys/new" element={<ProviderKeyCreatePage />} />
              <Route path="provider-keys/:id" element={<ProviderKeyDetailPage />} />
              <Route path="send-test" element={<SendTestPage />} />
            </Route>

            {REDIRECTS.map(({ from, to }) => (
              <Route key={from} path={from} element={<Redirect to={to} />} />
            ))}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>

        {/* global notification modal */}
        <Modal
          isOpen={!isNil(notificationState) && notificationState.show}
          onClose={() => globalDispatch(globalState.resetNotificationState())}
          title={notificationState?.title}
          size="small"
          testId="notification-modal"
          footer={
            notificationState?.redirectOnOk && (
              <ModalActions
                onDismiss={() => globalDispatch(globalState.resetNotificationState())}
                dismissTestId="notification-modal-close"
              />
            )
          }
        >
          <p className="text-sm text-gray-800 dark:text-white/90">{notificationState?.body}</p>
        </Modal>

        {/* session expired modal */}
        <Modal
          isOpen={session === 'expired'}
          onClose={() => signOut()}
          title="Session expired"
          size="small"
          testId="session-expired-modal"
          footer={
            <ModalActions
              onDismiss={() => signOut()}
              dismissLabel="Sign in again"
              dismissTestId="session-expired-modal-confirm"
            />
          }
        >
          <p className="text-sm text-gray-800 dark:text-white/90">
            Your session has expired. You will be redirected to the login page, to re-login.
          </p>
        </Modal>

        {/* session expiring modal */}
        <Modal
          isOpen={session === 'expiring'}
          onClose={() => handleSessionExpiringModalClose()}
          title="Session expiring soon"
          size="small"
          testId="session-expiring-modal"
          footer={
            <ModalActions onDismiss={() => signOut()} dismissLabel="Sign out">
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleSessionExpiringModalClose()}
                testId="session-expiring-modal-confirm"
              >
                Stay signed in
              </Button>
            </ModalActions>
          }
        >
          <p className="text-sm text-gray-800 dark:text-white/90">
            Your session is about to expire. Select Stay signed in to continue your session.
          </p>
        </Modal>
      </>
    );
  }
};
